import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { readUserSession } from '../../Auth/userSession';
import { getPreparedClient, LOGIN_REQUIRED_MESSAGE } from './aiClientData';
import AIChat from './AIChat';
import './AIClients.css';

const storageKey = 'garam-ai-client-instance-v1';
const noteKey = id => `garam-interview-notes:${id}:v1`;
const fields = [
    ['homework', '과제 수행 여부'], ['events', '새로 생긴 사건'],
    ['emotions', '현재 감정'], ['memo', '개인 메모'],
    ['summary', '이번 면담 요약'], ['cautions', '다음 면담 진행상 유의점'],
    ['tasks', '다음 회기의 확인 과제'],
];
function readNotes(id) {
    const data = JSON.parse(localStorage.getItem(noteKey(id)) || '{}');
    return Object.fromEntries(fields.map(([key]) => [key, typeof data?.[key] === 'string' ? data[key] : '']));
}

export default function AIInterview() {
    const { state } = useLocation();
    const [session, setSession] = useState(null);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    const [loggedIn, setLoggedIn] = useState(() => Boolean(readUserSession()));
    useEffect(() => {
        const updateAuth = () => setLoggedIn(Boolean(readUserSession()));
        window.addEventListener('garam-auth-change', updateAuth);
        return () => window.removeEventListener('garam-auth-change', updateAuth);
    }, []);
    useEffect(() => {
        let active = true;
        setError(''); setSession(null);
        if (!loggedIn) return;
        (async () => {
            try {
                const saved = state?.instance && state?.token
                    ? { id: state.instance.id, token: state.token }
                    : JSON.parse(localStorage.getItem(storageKey) || 'null');
                if (!saved?.id || !saved?.token) throw new Error('먼저 내담자 준비 단계에서 내담자를 선택해주세요.');
                const instance = await getPreparedClient(saved.id, saved.token);
                if (active) setSession({ instance, token: saved.token });
            } catch (failure) { if (active) setError(failure.message); }
        })();
        return () => { active = false; };
    }, [state, retry, loggedIn]);
    if (!loggedIn) return <main className="ai-clients-page"><div className="ai-clients-wrap"><p role="alert">{LOGIN_REQUIRED_MESSAGE}</p><Link to="/login">로그인하기</Link></div></main>;
    return <main className="ai-clients-page ai-interview-page"><div className="ai-clients-wrap">
        <ol className="ai-roadmap" aria-label="면담 연습 단계">
            <li><b>01</b> 내담자 준비</li>
            <li aria-current="step"><b>02</b> 면담</li>
            <li><b>03</b> 사례 개념화</li>
            <li><b>04</b> 종합 피드백</li>
        </ol>
        {error ? <div className="ai-alert" role="alert">{error} <button onClick={() => setRetry(value => value + 1)}>다시 시도</button> <Link to="/ai-clients">내담자 준비로 이동</Link></div>
            : session ? <Interview key={session.instance.id} {...session} /> : <p role="status">면담 정보를 불러오고 있습니다.</p>}
    </div></main>;
}

function Interview({ instance, token }) {
    const [initial] = useState(() => {
        try { return { notes: readNotes(instance.id), previous: instance.previousInstanceId ? readNotes(instance.previousInstanceId) : {}, error: '' }; }
        catch { return { notes: {}, previous: {}, error: '저장된 메모를 불러오지 못했습니다. 브라우저 저장 설정을 확인해주세요.' }; }
    });
    const client = instance.client;
    const returning = instance.meetingNumber > 1;
    return <>
        <article className="ai-profile">
            <span className="ai-kicker">{returning ? 'BEFORE THIS MEETING' : 'BEFORE THE FIRST MEETING'}</span>
            <h1>{client.name} 님의 이야기{returning && <span className="ai-meeting-number">{instance.meetingNumber}회기</span>}</h1>
            <dl><dt>기본 정보</dt><dd>{client.age}세 · {client.gender} · {client.occupation}</dd>
                {returning ? <>
                    <dt>지난 면담 요약</dt><dd>{initial.previous.summary || '지난 면담에 작성한 요약이 없습니다.'}</dd>
                    <dt>진행상 유의점</dt><dd>{initial.previous.cautions || '이전 면담의 다음 탐색 목표나 연습 항목이 아직 기록되지 않았습니다.'}</dd>
                    <dt>이번 회기의 확인 과제</dt><dd>{initial.previous.tasks || '이전 면담에 기록한 확인 과제가 없습니다. 면담에서 함께 확인해주세요.'}</dd>
                </> : <>
                    <dt>현재 상황</dt><dd>{client.background}</dd>
                    <dt>상담을 신청한 이유</dt><dd>{client.presentingConcern}</dd>
                    <dt>이번 사례의 변화 주제</dt><dd>{client.changeTarget}</dd>
                </>}
            </dl>
            <p className="ai-small">{returning ? '이전 회기 메모는 이 브라우저에 저장한 내용을 표시합니다. 사례 개념화·피드백 예시 화면의 내용은 자동 연결되지 않습니다.' : '교육을 위해 구성한 가상의 사례입니다. 내담자의 속마음과 관계 패턴은 이후 면담을 통해 탐색합니다.'}</p>
        </article>
        <AIChat instance={instance} token={token} />

    </>;
}
Interview.propTypes = { instance: PropTypes.object.isRequired, token: PropTypes.string.isRequired };
